import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/download.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableFirmwareVersionFilterApplied,
  fetchAllFirmwareVersions,
  fetchFirmwareVersionFilters,
  setFirmwareVersionLimit,
  setFirmwareVersionPage,
  setFirmwareVersionSortBy,
  setFirmwareVersionSortField,
  updateFirmwareVersionFilters,
} from '../../../redux/slices/FirmwareVersionSlice';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';
import AddFirmwareVersionDrawer from '../../ev-device-hub/components/drawers/AddFirmwareVersionDrawer';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Formats a byte count into a human-readable size string (KB/MB/GB).
 * @param {number} bytes - The size in bytes
 * @returns {string} A formatted string, e.g. "4.32 MB"
 */
const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 Bytes';
  const kilo = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const sizeIndex = Math.floor(Math.log(bytes) / Math.log(kilo));
  return `${parseFloat((bytes / kilo ** sizeIndex).toFixed(2))} ${sizes[sizeIndex]}`;
};

/**
 * Local firmware versions list component — displays uploaded firmware artifacts.
 * @returns {React.JSX.Element} Local firmware versions table
 */
function Local() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('station');
  const {
    firmwareVersions,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.firmwareVersion);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: null,
    header: false,
    content: null,
    notificationModal: false,
  });
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllFirmwareVersions({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllFirmwareVersions({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableFirmwareVersionFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setFirmwareVersionSortBy(sortData.sortBy));
    dispatch(setFirmwareVersionSortField(sortData.sortField));
    dispatch(
      fetchAllFirmwareVersions({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setFirmwareVersionPage(value.page));
    if (value.limit !== undefined) dispatch(setFirmwareVersionLimit(value.limit));
    dispatch(
      fetchAllFirmwareVersions({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes firmware version data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllFirmwareVersions({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Calls the API to delete a firmware version artifact
   * @param {string} firmwareId - The ID of the firmware artifact to delete
   */
  const handleConfirmDeleteFirmwareVersion = (firmwareId: string) => {
    deviceApi
      .deleteFirmwareArtifact(firmwareId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title={t('firmwareVersionDeletedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                handleRefresh();
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Triggers the delete confirmation warning modal for a firmware version
   * @param {string} firmwareId - The ID of the firmware artifact
   */
  const handleDeleteFirmwareVersionClick = (firmwareId: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={firmwareId}
          title={t('deleteFirmwareVersionConfirmation')}
          description={t('deleteFirmwareVersionConfirmationDesc')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleConfirmDeleteFirmwareVersion(id)}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Downloads a firmware binary as a Blob and triggers a browser save, using
   * the same anchor-click mechanic as shared-functions/ExportFile.ts's exportCSV.
   * @param {any} currentRow - row data
   */
  const handleDownloadFirmware = (currentRow: any) => {
    setDownloadingId(currentRow._id);
    deviceApi
      .downloadFirmwareArtifact(currentRow._id)
      .then((blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.setAttribute(
          'download',
          currentRow.fileName || `firmware-${currentRow.version}.bin`,
        );
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      })
      .catch((error: any) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      })
      .finally(() => setDownloadingId(null));
  };

  const column = [
    {
      label: t('version'),
      accessorKey: 'version',
    },
    {
      label: t('brandModel'),
      accessorKey: 'brandName',
      /**
       * Renders brand/model stacked cell, mirroring renderBrandModel in ev-device-hub
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <div className="brand-model-cell">
          <span className="brand-name">
            {currentRow.brandName || currentRow.brandId?.name || '-'}
          </span>
          <span className="model-name">
            {currentRow.modelName || currentRow.modelId?.name || '-'}
          </span>
        </div>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status',
      /**
       * Renders firmware status badge (Active/Uploading/UploadFailed)
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => {
        const status = currentRow.status || 'Uploading';
        const badgeClassMap: Record<string, string> = {
          Active: 'status-green',
          Uploading: 'status-neutral',
          UploadFailed: 'status-red',
        };
        const labelMap: Record<string, string> = {
          Active: t('active'),
          Uploading: t('uploading'),
          UploadFailed: t('uploadFailed'),
        };
        return (
          <span className={`firmware-status-badge ${badgeClassMap[status] || 'status-neutral'}`}>
            {labelMap[status] || status}
          </span>
        );
      },
    },
    {
      label: t('fileSize'),
      accessorKey: 'sizeBytes',
      /**
       * Renders human-readable file size
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{formatFileSize(Number(currentRow.sizeBytes))}</span>,
    },
    {
      label: t('uploadedBy'),
      accessorKey: 'uploadedBy',
      /**
       * Renders the uploader's name
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>{currentRow.uploadedBy?.name || currentRow.uploadedBy || '-'}</span>
      ),
    },
    {
      label: t('uploadedAt'),
      accessorKey: 'uploadedAt',
      /**
       * Renders the formatted upload date/time
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>{currentRow.uploadedAt ? ConvertUTCtoDateTime(currentRow.uploadedAt) : '-'}</span>
      ),
    },
    {
      label: t('actions'),
      accessorKey: 'actions',
      sortAllow: false,
      columnWidth: 80,
      sticky: 'right' as const,
      /**
       * Renders row action buttons (download + delete)
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <div className="action-buttons-wrap">
          {currentRow.status === 'Active' && (
            <div
              role="presentation"
              className={`action-button ${downloadingId === currentRow._id ? 'disabled' : ''}`}
              onClick={(event) => {
                event.stopPropagation();
                if (downloadingId !== currentRow._id) handleDownloadFirmware(currentRow);
              }}>
              <DownloadIcon className="icon download" />
            </div>
          )}
          {/* TEMP: reusing devicesBrandsAdd until firmwareArtifactsDelete is provisioned on the backend */}
          {permissionCheck('devicesBrandsAdd') && (
            <div
              role="presentation"
              className="action-button"
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteFirmwareVersionClick(currentRow._id);
              }}>
              <DeleteIcon className="icon delete" />
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />

      <CustomTable
        dataKey="FirmwareVersionManagement"
        header={column}
        data={firmwareVersions}
        serialNo
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        height={310}
        created
        lastUpdated
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handlePageLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateFirmwareVersionFilters}
        filterListToDispatch={fetchFirmwareVersionFilters}
        actionButton
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
        refershCurrentPages={handleRefresh}
        rightTopContent={
          <div className="table-bottom-button-list">
            {/* TEMP: reusing devicesBrandsAdd until firmwareArtifactsAdd is provisioned on the backend */}
            {permissionCheck('devicesBrandsAdd') && (
              <div className="table-button-item">
                <MyButton
                  label={t('uploadFirmware')}
                  buttonType="submit"
                  onClickFunc={() => setDrawerOpen(true)}
                />
              </div>
            )}
          </div>
        }
      />

      <AddFirmwareVersionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onFirmwareVersionCreated={() => {
          setDrawerOpen(false);
          handleRefresh();
        }}
      />
    </>
  );
}

export default Local;
